// The duty-type picker, in the shape the operations screens use.
//
// A coloured pill that opens a small menu, rather than a native <select>: the
// operations board and assignment dashboard both read this way, and the client
// asked for the billing screens to match. Colours come from the shared duty
// palette, so all three stay in step.
//
// The menu is rendered through a portal rather than positioned inside the cell.
// The bills table scrolls horizontally, and a container that scrolls on one
// axis clips the other — an absolutely positioned menu in that cell would be
// cut off near the bottom of the table, or would force a vertical scrollbar.
// Fixed to the pill's position on screen, it can overflow the table freely.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dutyPalette } from '../constants/shifts';

interface Props {
  value?: string | null;
  /** Every duty type on offer, in the order to show them. */
  options: string[];
  onChange: (next: string) => void;
  title?: string;
}

const ITEM_HEIGHT = 30;   // one menu row, for deciding whether it fits below

const DutyTypeSelect: React.FC<Props> = ({ value, options, onChange, title }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Place the menu under the pill, in viewport coordinates — flipped above when
  // the pill is near the bottom of the window, which it often is on a long list.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const height = options.length * ITEM_HEIGHT + 8;
    setPos({
      top: window.innerHeight - r.bottom < height + 12 ? r.top - height - 6 : r.bottom + 6,
      left: r.left,
    });
  }, [open, options.length]);

  // Close on an outside click, on Escape, and on any scroll or resize: the menu
  // is pinned to where the pill was, so scrolling would strand it.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);   // capture, to catch inner scrollers
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const pal = dutyPalette(value);

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        // stopPropagation, or the document listener above would treat the very
        // click that opened the menu as the outside click that closes it.
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title={title}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: '3px 8px 3px 9px', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em',
          background: pal.bg, color: pal.fg, border: `1.5px solid ${pal.border}`,
          cursor: 'pointer', outline: 'none', whiteSpace: 'nowrap',
          fontFamily: 'inherit', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        {value || '—'}
        <svg viewBox="0 0 10 10" width="8" height="8" fill="none" stroke={pal.fg}
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1,3 5,7 9,3" />
        </svg>
      </button>

      {open && pos && createPortal(
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: pos.top, left: pos.left,
            backgroundColor: 'white', borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
            padding: '4px', zIndex: 3000, minWidth: '160px',
          }}
        >
          {options.map((opt) => {
            const selected = opt === value;
            const optPal = dutyPalette(opt);
            return (
              <div
                key={opt}
                onClick={() => { setOpen(false); onChange(opt); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', borderRadius: '7px',
                  fontSize: '0.74rem', fontWeight: selected ? 700 : 500,
                  color: selected ? '#1e293b' : '#334155',
                  backgroundColor: selected ? '#f1f5f9' : 'transparent',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!selected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  if (!selected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                {/* The duty's own colour, so the menu reads the same way the
                    pills in the column do. */}
                <span style={{
                  width: '9px', height: '9px', borderRadius: '50%',
                  backgroundColor: optPal.bg, border: `1.5px solid ${optPal.fg}`,
                  flexShrink: 0,
                }} />
                {opt}
                {selected && (
                  <svg style={{ marginLeft: 'auto' }} viewBox="0 0 12 12" width="10" height="10"
                    fill="none" stroke="#1e293b" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DutyTypeSelect;
